import { useAuthStore, StaffRole, Permission, type StaffUser } from './stores/auth.store';
import { authService } from './services/auth.service';
import { apiService, type UserInfoResponse } from './services/api.service';

/**
 * DEV-ONLY authentication helpers.
 *
 * The OAuth redirect is locked server-side (login.paladium.games always returns
 * to the production domain), so we can't run the real login flow on localhost.
 * Instead, in dev we let you paste a REAL JWT token (copied from the production
 * site's localStorage key `recubebe_xbox_token`) and rebuild a real session from
 * it — so the backend accepts the `Authorization: Bearer` header normally.
 *
 * All of this is guarded by `import.meta.env.DEV` and tree-shaken out of prod builds.
 */

/** Storage key used by auth.service for the raw JWT. */
const TOKEN_KEY = 'recubebe_xbox_token';

/** Map LuckPerms group name to StaffRole enum (mirrors AuthCallback). */
function mapGroupToRole(group: string): StaffRole {
  switch (group.toLowerCase()) {
    case 'admin':
    case 'administrateur':
      return StaffRole.ADMIN;
    case 'moderator':
    case 'moderateur':
    case 'mod':
      return StaffRole.MODERATOR;
    case 'builder':
    case 'build':
      return StaffRole.BUILDER;
    default:
      return StaffRole.HELPER;
  }
}

export interface DevLoginResult {
  ok: boolean;
  error?: string;
  /**
   * Motif du refus de check-access (no_permission, invalid_token, api_error...). `api_error` : le
   * backend du panel n'a pas pu répondre (503, MongoDB en panne, réseau), l'accès n'est pas refusé.
   */
  reason?: string;
  /**
   * `api_error` sur le 503 de la base des grades seulement (code GRADE_BACKEND_UNAVAILABLE) :
   * identifiant de compte à ajouter au secret PANEL_EMERGENCY_ADMINS pour obtenir l'accès d'urgence
   * (celui que compare l'API).
   */
  emergencyId?: string;
}

/** Session du panel construite à partir de /api/auth/me (connexion, puis relectures du layout). */
export function staffUserFromInfo(u: UserInfoResponse): StaffUser {
  return {
    id: u.xuid,
    xuid: u.xuid,
    gamertag: u.gamertag,
    role: u.isAdmin ? StaffRole.ADMIN : mapGroupToRole(u.rank),
    displayRole: u.displayName,
    permissions: (u.permissions ?? []) as Permission[],
    isAdmin: !!u.isAdmin,
    gradeIds: u.gradeIds ?? [],
    panelPermissions: u.panelPermissions ?? [],
    manageAccess: !!u.manageAccess,
    canAssignGrades: !!u.canAssignGrades,
    assignableGradeIds: u.assignableGradeIds ?? [],
    adminProjects: u.adminProjects ?? [],
    emergency: !!u.emergency,
    gameGroups: u.gameGroups ?? {},
  };
}

/**
 * Log in locally using a real JWT token, reproducing the AuthCallback success flow:
 * validate token -> check panel access -> fetch user info -> populate the store.
 */
export async function loginWithDevToken(rawToken: string): Promise<DevLoginResult> {
  const token = rawToken.trim();
  if (!token) return { ok: false, error: 'Token vide.' };

  if (!authService.isTokenValid(token)) {
    return { ok: false, error: 'Token invalide ou expiré.' };
  }

  // Make the token available to apiService before the access/user-info calls.
  authService.setToken(token);

  const payload = authService.decodeToken(token);
  if (!payload) {
    authService.clearToken();
    return { ok: false, error: 'Impossible de décoder le token.' };
  }

  const access = await authService.checkPanelAccess();
  if (!access.hasAccess) {
    authService.clearToken();
    return {
      ok: false,
      error: `Accès au panel refusé (${access.reason ?? 'raison inconnue'}).`,
      reason: access.reason,
      emergencyId: access.emergencyId,
    };
  }

  const { setAuth } = useAuthStore.getState();
  const userInfo = await apiService.getCurrentUser();

  let user: StaffUser;
  if (userInfo.data) {
    user = staffUserFromInfo(userInfo.data);
  } else {
    // Fallback to basic info from the token.
    user = {
      id: payload.xuid,
      xuid: payload.xuid,
      gamertag: payload.gamertag,
      role: StaffRole.HELPER,
      permissions: [Permission.VIEW_DASHBOARD],
      // Accès d'urgence accordé par check-access : le bandeau s'affiche même si /me a échoué.
      emergency: !!access.emergency,
    };
  }

  setAuth(user, token, '');
  return { ok: true };
}

/**
 * On startup (dev only): if a valid real token is already stored but the auth
 * store isn't populated (e.g. token pasted via devtools), rebuild the session.
 * No-op in production.
 */
export function applyDevAuthBootstrap(): void {
  if (!import.meta.env.DEV) return;

  const alreadyAuth = useAuthStore.getState().isAuthenticated;
  if (alreadyAuth) return;

  const token = localStorage.getItem(TOKEN_KEY);
  if (token && authService.isTokenValid(token)) {
    void loginWithDevToken(token);
  }
}
